import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const AuthContext = createContext(null);
const AUTH_STORAGE_KEY = "saudi-luxury-real-estate-auth";

const readInitialSession = () => {
  const stored = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!stored) {
    return null;
  }

  try {
    const parsed = JSON.parse(stored);
    if (!parsed?.access_token || !parsed?.user) {
      return null;
    }

    // Note: We no longer hard-fail on expired tokens here because
    // the API interceptor can refresh tokens within the refresh window
    return parsed;
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(readInitialSession);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState(null);

  // Listen for session expiry events from the API interceptor
  useEffect(() => {
    const handleSessionExpired = (event) => {
      setSession(null);
      setSessionExpiredMessage(event.detail?.message || "Session expired. Please login again.");
    };

    window.addEventListener("session-expired", handleSessionExpired);
    return () => window.removeEventListener("session-expired", handleSessionExpired);
  }, []);

  // Sync session state with localStorage changes (for multi-tab support)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === AUTH_STORAGE_KEY) {
        if (event.newValue) {
          try {
            const parsed = JSON.parse(event.newValue);
            if (parsed?.access_token && parsed?.user) {
              setSession(parsed);
              setSessionExpiredMessage(null);
            }
          } catch {
            // Invalid JSON, ignore
          }
        } else {
          setSession(null);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const login = (payload) => {
    const nextSession = {
      access_token: payload.access_token,
      csrf_token: payload.csrf_token,
      expires_at: payload.expires_at,
      user: payload.user,
    };
    setSession(nextSession);
    setSessionExpiredMessage(null);
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextSession));
  };

  const logout = () => {
    setSession(null);
    setSessionExpiredMessage(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  const updateUser = (partialUser) => {
    setSession((prev) => {
      if (!prev?.user) {
        return prev;
      }

      const nextSession = {
        ...prev,
        user: {
          ...prev.user,
          ...partialUser,
        },
      };
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextSession));
      return nextSession;
    });
  };

  const clearSessionExpiredMessage = () => {
    setSessionExpiredMessage(null);
  };

  const value = useMemo(
    () => ({
      session,
      user: session?.user || null,
      isAuthenticated: Boolean(session?.access_token && session?.user),
      sessionExpiredMessage,
      login,
      logout,
      updateUser,
      clearSessionExpiredMessage,
    }),
    [session, sessionExpiredMessage],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
